import { Component, OnInit } from '@angular/core';
import { AngularEditorConfig } from '@kolkov/angular-editor';
import { NgxSpinnerService } from 'ngx-spinner';
import Swal from 'sweetalert2';
import { RestApiProvider } from 'src/app/services/shared/api.service';

const MAX_IMAGENES = 10;
const MAX_PESO_IMAGEN = 5 * 1024 * 1024;
const TIPOS_IMAGEN = ['image/jpeg', 'image/png', 'image/webp'];

// Imagen ya guardada (con id) o pendiente de subir (con file y vista previa local)
interface ImagenActualizacion {
  id?: number;
  imagen: string;
  file?: File;
}

@Component({
  selector: 'app-actualizaciones',
  templateUrl: './actualizaciones.component.html',
  styleUrls: ['./actualizaciones.component.css']
})
export class ActualizacionesComponent implements OnInit {
  actualizaciones: any[] = [];
  total = 0;
  page = 1;
  pageSize = 10;
  buscar = '';

  // null = listado; objeto = formulario de alta o edición
  form: any = null;
  imagenes: ImagenActualizacion[] = [];
  maxImagenes = MAX_IMAGENES;

  editorConfig: AngularEditorConfig = {
    editable: true,
    spellcheck: true,
    height: 'auto',
    minHeight: '220px',
    placeholder: 'Detalle completo de la actualización...',
    translate: 'no',
    enableToolbar: true,
    showToolbar: true,
  };

  constructor(
    private rest_api: RestApiProvider,
    private spinner: NgxSpinnerService
  ) {}

  ngOnInit() {
    this.cargar();
  }

  get totalPaginas(): number {
    return Math.max(1, Math.ceil(this.total / this.pageSize));
  }

  cargar(page: number = this.page) {
    this.spinner.show();
    this.rest_api.getActualizaciones(page, this.buscar).then((response: any) => {
      this.actualizaciones = response['results'] || [];
      this.total = response['count'] || 0;
      this.page = page;
      this.spinner.hide();
    }).catch(() => {
      this.spinner.hide();
      Swal.fire('Error', 'No se pudieron cargar las actualizaciones.', 'error');
    });
  }

  buscarActualizaciones() {
    this.cargar(1);
  }

  // La fecha de lanzamiento es de calendario: se arma del texto, sin pasar por Date,
  // para que la zona horaria no la recorra un día.
  fecha(valor: string): string {
    if (!valor) { return ''; }
    const partes = valor.substring(0, 10).split('-');
    return partes.length === 3 ? partes[2] + '/' + partes[1] + '/' + partes[0] : valor;
  }

  private hoy(): string {
    const d = new Date();
    const dos = (n: number) => (n < 10 ? '0' : '') + n;
    return d.getFullYear() + '-' + dos(d.getMonth() + 1) + '-' + dos(d.getDate());
  }

  nueva() {
    this.form = { id: null, fecha_lanzamiento: this.hoy(), titulo: '', resumen: '', descripcion: '', publicada: false };
    this.imagenes = [];
  }

  editar(item: any) {
    this.form = {
      id: item.id,
      fecha_lanzamiento: item.fecha_lanzamiento,
      titulo: item.titulo,
      resumen: item.resumen,
      descripcion: item.descripcion || '',
      publicada: item.publicada
    };
    this.imagenes = (item.imagenes || []).map((img: any) => ({ id: img.id, imagen: img.imagen }));
  }

  cancelar() {
    this.form = null;
    this.imagenes = [];
  }

  onImagenes(event: any) {
    const archivos: File[] = Array.prototype.slice.call(event.target.files || []);
    event.target.value = '';
    const errores: string[] = [];
    for (const archivo of archivos) {
      if (this.imagenes.length >= MAX_IMAGENES) {
        errores.push('Máximo ' + MAX_IMAGENES + ' imágenes por actualización.');
        break;
      }
      if (TIPOS_IMAGEN.indexOf(archivo.type) === -1) {
        errores.push(archivo.name + ': formato no permitido (JPG, PNG o WEBP).');
        continue;
      }
      if (archivo.size > MAX_PESO_IMAGEN) {
        errores.push(archivo.name + ': supera el máximo de 5 MB.');
        continue;
      }
      const item: ImagenActualizacion = { imagen: '', file: archivo };
      this.imagenes.push(item);
      const reader = new FileReader();
      reader.onload = (e: any) => { item.imagen = e.target.result; };
      reader.readAsDataURL(archivo);
    }
    if (errores.length) {
      Swal.fire('Imágenes', errores.join('<br>'), 'warning');
    }
  }

  moverImagen(index: number, delta: number) {
    const destino = index + delta;
    if (destino < 0 || destino >= this.imagenes.length) { return; }
    const item = this.imagenes[index];
    this.imagenes[index] = this.imagenes[destino];
    this.imagenes[destino] = item;
  }

  quitarImagen(index: number) {
    const item = this.imagenes[index];
    if (!item.id) {
      this.imagenes.splice(index, 1);
      return;
    }
    Swal.fire({
      title: '¿Eliminar la imagen?',
      text: 'Se quitará de la actualización.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Eliminar',
      cancelButtonText: 'Cancelar'
    }).then(result => {
      if (!result.value) { return; }
      this.spinner.show();
      this.rest_api.deleteActualizacionImagen(this.form.id, item.id).then(() => {
        this.imagenes = this.imagenes.filter(img => img !== item);
        this.spinner.hide();
      }).catch(() => {
        this.spinner.hide();
        Swal.fire('Error', 'No se pudo eliminar la imagen.', 'error');
      });
    });
  }

  private mensajeError(err: any, defecto: string): string {
    const error = err && err.error;
    if (!error) { return defecto; }
    if (typeof error === 'string') { return defecto; }
    if (error.error) { return error.error; }
    const mensajes: string[] = [];
    for (const key of Object.keys(error)) {
      mensajes.push([].concat(error[key]).join(' '));
    }
    return mensajes.length ? mensajes.join(' ') : defecto;
  }

  guardar() {
    const titulo = (this.form.titulo || '').trim();
    const resumen = (this.form.resumen || '').trim();
    if (!this.form.fecha_lanzamiento || !titulo || !resumen) {
      Swal.fire('Faltan datos', 'Fecha de lanzamiento, título y resumen son obligatorios.', 'warning');
      return;
    }
    const payload = {
      fecha_lanzamiento: this.form.fecha_lanzamiento,
      titulo: titulo,
      resumen: resumen,
      descripcion: this.form.descripcion || '',
      publicada: !!this.form.publicada
    };

    this.spinner.show();
    this.rest_api.saveActualizacion(payload, this.form.id)
      .then((guardada: any) => {
        // Si las imágenes fallan, la actualización ya existe: se queda en edición
        this.form.id = guardada['id'];
        return this.guardarImagenes(guardada['id']);
      })
      .then(() => {
        this.spinner.hide();
        this.cancelar();
        this.cargar(this.page);
        Swal.fire('Guardado', 'La actualización se guardó correctamente.', 'success');
      })
      .catch(err => {
        this.spinner.hide();
        Swal.fire('Error', this.mensajeError(err, 'No se pudo guardar la actualización.'), 'error');
      });
  }

  // Sube las pendientes y deja el orden tal como se ve en pantalla
  private guardarImagenes(id: number): Promise<any> {
    const pendientes = this.imagenes.filter(img => !img.id);
    const subir: Promise<any> = pendientes.length
      ? this.rest_api.uploadActualizacionImagenes(id, pendientes.map(img => img.file))
      : Promise.resolve(null);

    return subir.then((response: any) => {
      if (response) {
        // El servidor agrega las nuevas al final y en el mismo orden en que se enviaron
        const guardadas: any[] = response['imagenes'] || [];
        const nuevas = guardadas.slice(guardadas.length - pendientes.length);
        pendientes.forEach((img, i) => {
          img.id = nuevas[i].id;
          img.imagen = nuevas[i].imagen;
          delete img.file;
        });
      }
      if (this.imagenes.length < 2) { return null; }
      return this.rest_api.ordenarActualizacionImagenes(id, this.imagenes.map(img => img.id));
    });
  }

  cambiarEstatus(item: any) {
    const publicar = !item.publicada;
    this.spinner.show();
    this.rest_api.saveActualizacion({ publicada: publicar }, item.id).then(() => {
      item.publicada = publicar;
      this.spinner.hide();
    }).catch(() => {
      this.spinner.hide();
      Swal.fire('Error', 'No se pudo cambiar el estatus.', 'error');
    });
  }

  eliminar(item: any) {
    Swal.fire({
      title: '¿Eliminar la actualización?',
      text: 'Se elimina de forma definitiva. Para ocultarla temporalmente use el estatus "No publicada".',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Eliminar',
      cancelButtonText: 'Cancelar'
    }).then(result => {
      if (!result.value) { return; }
      this.spinner.show();
      this.rest_api.deleteActualizacion(item.id).then(() => {
        this.spinner.hide();
        const quedaVacia = this.actualizaciones.length === 1 && this.page > 1;
        this.cargar(quedaVacia ? this.page - 1 : this.page);
      }).catch(() => {
        this.spinner.hide();
        Swal.fire('Error', 'No se pudo eliminar la actualización.', 'error');
      });
    });
  }
}
